import React, { useCallback, useMemo, useState } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { Autocomplete } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';
import { useLearningContentsQuery } from '@/modules/corporate/graphql/queries/generated/contents';
import { useCourse } from '@/modules/corporate/modules/course/courseContext';
import { QuizContentTypeSearchFragment } from '@/modules/corporate/modules/course/graphql/fragments/generated/QuizContentTypeSearch';
import { OrderEnum, ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';

import { RenderInput, renderOption } from './renderers';
import { ICreateOption, IOption, SearchProps } from './types';

const CREATE_OPTION: ICreateOption = { type: 'create' };

const getOptionLabel = (option: IOption) => (option.type === 'create' ? '' : option.name);

const isOptionEqualToValue = (option: IOption, value: IOption) => {
  // The create option should never be considered "selected" so it stays clickable
  if (option.type === 'create' || value.type === 'create') {
    return false;
  }
  return option.id === value.id;
};

function Search(props: SearchProps) {
  const { t } = useTranslation();
  const { updateAttribute, onCreateNew } = props;
  const [inputValue, setValue, debouncedValue] = useDebounceState('');
  const [selectedValue, setSelectedValue] = useState<IOption | undefined>(undefined);

  const { course } = useCourse();

  const [{ data }] = useLearningContentsQuery({
    variables: {
      filters: {
        types: [ResourceTypeEnum.Quiz],
        sourceLanguageContent: true,
        name: debouncedValue,
        status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
        shareable: course.isSent ? true : undefined,
      },
      sort: [{ field: 'relevance', order: OrderEnum.Desc }],
    },
  });

  const onChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: IOption | null) => {
      if (!value) {
        return;
      }
      if (value.type === 'create') {
        onCreateNew();
        // Reset so it can be selected again
        setSelectedValue(undefined);
        return;
      }
      setSelectedValue(value);
      updateAttribute({ contentId: value.id });
    },
    [onCreateNew, updateAttribute],
  );

  const onInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: string) => {
      setValue(value);
    },
    [setValue],
  );

  const options: IOption[] = useMemo(() => {
    const quizOptions: IOption[] = data?.contents.edges?.length
      ? data.contents.edges.map(({ node }) => {
          const { id, name, quizQuestionsConfig } = node as QuizContentTypeSearchFragment;

          return {
            type: 'quiz' as const,
            id,
            name,
            questionsCount: quizQuestionsConfig.questionCount,
          };
        })
      : [];

    return [CREATE_OPTION, ...quizOptions];
  }, [data?.contents.edges]);

  // Server-side filtering via debouncedValue handles quiz search.
  // Pass all options through so the create option is always visible.
  const filterOptions = useCallback((opts: IOption[]) => opts, []);

  const _renderInput = useCallback(
    (params: any) =>
      RenderInput({
        params,
        onBlurPlaceholder: t('Course:Builder.AttachQuiz'),
        onFocusPlaceholder: t('Course:Builder.SearchQuiz'),
      }),
    [t],
  );

  return (
    <Autocomplete
      disableClearable
      disablePortal
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      optionLabelRenderer={(option, state) => renderOption({ option, state, t })}
      options={options}
      popupIcon={null}
      renderInput={_renderInput}
      size="large"
      value={selectedValue}
      onChange={onChange}
      onInputChange={onInputChange}
      onKeyDown={stopEventPropagation}
      onKeyPress={stopEventPropagation}
      onPaste={stopEventPropagation}
    />
  );
}

export default Search;
